import { useState, useRef, useCallback, useEffect } from 'react'
import { message } from 'antd'
import { getEphemeralToken } from '../api/realtime-api'
import { setupAudioContext } from '../utils/webrtc-utils'

// Add singleton instance tracking
let activeInstance = null

/**
 * Hook to manage WebRTC connection for AI voice assistant
 * @param {Object} options - Configuration options
 * @param {string} options.systemContext - System context for the AI
 * @param {Function} options.onMessage - Callback for handling messages
 * @returns {Object} WebRTC connection state and methods
 */
const useWebRTCConnection = ({ systemContext, onMessage }) => {
  const [isConnecting, setIsConnecting] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [isListening, setIsListening] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [sessionId, setSessionId] = useState(null)
  const [isAISpeaking, setIsAISpeaking] = useState(false)
  const [userSpeaking, setUserSpeaking] = useState(false)

  const MAX_RETRIES = 3
  const peerConnection = useRef(null)
  const dataChannel = useRef(null)
  const audioElement = useRef(new Audio())
  const audioContext = useRef(null)
  const gainNode = useRef(null)
  const localStream = useRef(null)
  const sessionInitSent = useRef(false)
  const isConnectedRef = useRef(false)

  // Track initialization status
  const initializationStatus = useRef({
    dataChannelReady: false,
    connectionReady: false
  })

  // Track connection stability
  const connectionStability = useRef({
    isStable: false,
    stabilityTimeout: null
  })

  // Update connected ref when state changes
  useEffect(() => {
    isConnectedRef.current = isConnected
  }, [isConnected])

  // Prevent multiple instances
  useEffect(() => {
    if (activeInstance) {
      console.warn('Multiple instances of WebRTCAssistant detected. Only one instance should be active at a time.')
      cleanup(true)
      return
    }
    activeInstance = true
    return () => {
      activeInstance = null
    }
    // eslint-disable-next-line
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => cleanup()
    // eslint-disable-next-line
  }, [])

  /**
   * Cleans up all WebRTC resources
   * @param {boolean} isUserInitiated - Whether cleanup was initiated by user
   */
  const cleanup = useCallback((isUserInitiated = false) => {
    if (!isUserInitiated && isConnectedRef.current) {
      return
    }

    // Clean up audio element
    try {
      if (audioElement.current) {
        audioElement.current.onplay = null
        audioElement.current.onpause = null
        audioElement.current.onwaiting = null
        audioElement.current.onstalled = null
        audioElement.current.onsuspend = null
        audioElement.current.oncanplay = null
        audioElement.current.onerror = null

        audioElement.current.pause()

        if (audioElement.current.srcObject) {
          const tracks = audioElement.current.srcObject.getTracks()
          tracks.forEach((track) => {
            track.stop()
            track.enabled = false
          })
          audioElement.current.srcObject = null
        }

        audioElement.current.src = ''
        audioElement.current.load()
        audioElement.current = null
      }
    } catch (error) {
      console.error('Error during audio cleanup:', error)
    }

    // Clean up audio context
    try {
      if (audioContext.current) {
        audioContext.current.close().catch(console.error)
        audioContext.current = null
      }
    } catch (error) {
      console.error('Error during audio context cleanup:', error)
    }

    if (gainNode.current) {
      try {
        gainNode.current.disconnect()
        gainNode.current = null
      } catch (error) {
        console.error('Error during gain node cleanup:', error)
      }
    }

    // Clean up data channel
    if (dataChannel.current) {
      try {
        dataChannel.current.onclose = null
        dataChannel.current.onerror = null
        dataChannel.current.onmessage = null
        dataChannel.current.close()
        dataChannel.current = null
      } catch (error) {
        console.error('Error during data channel cleanup:', error)
      }
    }

    // Clean up peer connection
    if (peerConnection.current) {
      try {
        peerConnection.current.onconnectionstatechange = null
        peerConnection.current.oniceconnectionstatechange = null
        peerConnection.current.ontrack = null

        const transceivers = peerConnection.current.getTransceivers()
        transceivers.forEach((transceiver) => {
          if (transceiver.stop) {
            transceiver.stop()
          }
        })

        peerConnection.current.close()
        peerConnection.current = null
      } catch (error) {
        console.error('Error during peer connection cleanup:', error)
      }
    }

    // Reset all state
    initializationStatus.current = {
      dataChannelReady: false,
      connectionReady: false
    }
    sessionInitSent.current = false
    setSessionId(null)
    setIsConnected(false)
    setIsListening(false)
    setRetryCount(0)
    setIsAISpeaking(false)
    setUserSpeaking(false)
  }, [])

  /**
   * Handles connection state changes
   */
  const handleConnectionStateChange = useCallback(() => {
    const state = peerConnection.current?.connectionState

    if (state === 'connected') {
      initializationStatus.current.connectionReady = true
    } else if (state === 'disconnected' || state === 'failed') {
      connectionStability.current.isStable = false
      cleanup()
    }
  }, [cleanup])

  /**
   * Sets up the data channel for communication
   */
  const setupDataChannel = useCallback(() => {
    if (!peerConnection.current) {
      console.error('setupDataChannel: No peer connection available')
      return
    }

    dataChannel.current = peerConnection.current.createDataChannel('text', {
      ordered: true
    })

    dataChannel.current.onopen = () => {
      initializationStatus.current.dataChannelReady = true
      setIsConnected(true)

      // Send session init first
      sendSessionInit()
    }

    dataChannel.current.onclose = () => {
      cleanup()
    }

    dataChannel.current.onerror = (error) => {
      console.error('=== Data Channel Error ===')
      console.error('Error object:', error)
      console.error('Error type:', error.type)
      console.error('Error message:', error.message)
      console.error('Channel state:', dataChannel.current?.readyState)
      console.error('Stack:', new Error().stack)
    }

    dataChannel.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)

        // Handle message state updates
        switch (data.type) {
          case 'session.created':
            setSessionId(data.session.id)
            break

          case 'input_audio_buffer.speech_started':
            setUserSpeaking(true)
            break

          case 'input_audio_buffer.speech_stopped':
            setUserSpeaking(false)
            break

          case 'output_audio_buffer.audio_started':
            setIsAISpeaking(true)
            break

          case 'response.audio.done':
            setIsAISpeaking(false)
            break
        }

        // Pass the message to the parent component
        onMessage?.(data)
      } catch (error) {
        console.error('=== Error in handleMessage ===')
        console.error('Error parsing/handling message:', error)
        console.error('Raw message data:', event.data)
        console.error('Stack trace:', error.stack)
      }
    }
    // eslint-disable-next-line
  }, [cleanup, onMessage])

  /**
   * Sends the initial session setup message
   */
  const sendSessionInit = useCallback(async () => {
    if (!dataChannel.current || sessionInitSent.current) {
      return
    }

    try {
      // First send system context
      const systemContextMessage = {
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'system',
          content: [
            {
              type: 'input_text',
              text:
                systemContext ||
                `You are an AI Assistant.

INITIAL GREETING:
Only say "Hello! How can I help you today?"

INTERACTION GUIDELINES:
- Keep responses focused and concise
- Only explain features when asked "What can you do?"

Remember: Always wait for explicit questions before providing information.`
            }
          ]
        }
      }

      await dataChannel.current.send(JSON.stringify(systemContextMessage))

      // Start the conversation
      const startMessage = {
        type: 'response.create'
      }
      await dataChannel.current.send(JSON.stringify(startMessage))

      sessionInitSent.current = true
    } catch (error) {
      console.error('Error in sendSessionInit:', error)
      throw error
    }
  }, [systemContext])

  /**
   * Initializes the WebRTC connection
   */
  const initWebRTC = useCallback(
    async (stream) => {
      try {
        cleanup()
        setIsConnecting(true)

        // Set up audio context
        const { audioContext: newAudioContext, gainNode: newGainNode } = setupAudioContext()
        audioContext.current = newAudioContext
        gainNode.current = newGainNode

        // Get ephemeral token
        const tokenResponse = await getEphemeralToken()

        if (!tokenResponse?.client_secret?.value) {
          throw new Error('Invalid token format received')
        }

        const EPHEMERAL_KEY = tokenResponse.client_secret.value

        // Create peer connection
        peerConnection.current = new RTCPeerConnection({
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
            { urls: 'stun:stun2.l.google.com:19302' }
          ]
        })

        // Monitor connection state changes
        peerConnection.current.onconnectionstatechange = handleConnectionStateChange
        peerConnection.current.oniceconnectionstatechange = () => {}

        // Add the audio stream to the peer connection
        if (stream) {
          localStream.current = stream
          stream.getTracks().forEach((track) => {
            peerConnection.current.addTrack(track, stream)
          })
        } else {
          throw new Error('No audio stream available')
        }

        // Handle incoming audio
        peerConnection.current.ontrack = (event) => {
          try {
            if (audioElement.current) {
              audioElement.current.pause()
              audioElement.current.srcObject = null
            }

            audioElement.current = new Audio()
            audioElement.current.srcObject = event.streams[0]
            audioElement.current.autoplay = true
            audioElement.current.playsInline = true

            const playPromise = audioElement.current.play()
            if (playPromise !== undefined) {
              playPromise.catch((error) => {
                console.error('Error playing audio:', error)
                document.addEventListener(
                  'click',
                  () => {
                    audioElement.current?.play().catch((e) => console.error('Play after interaction failed:', e))
                  },
                  { once: true }
                )
              })
            }
          } catch (error) {
            console.error('Error setting up audio track:', error)
          }
        }

        // Set up data channel
        setupDataChannel()

        // Create and set local description
        const offer = await peerConnection.current.createOffer({
          offerToReceiveAudio: true
        })
        await peerConnection.current.setLocalDescription(offer)

        // Wait for ICE gathering
        await Promise.race([
          new Promise((resolve) => {
            const checkState = () => {
              if (peerConnection.current?.iceGatheringState === 'complete') {
                resolve()
              }
            }
            peerConnection.current.addEventListener('icegatheringstatechange', checkState)
            checkState()
          }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('ICE gathering timeout')), 5000))
        ]).catch((error) => {})

        // Connect to OpenAI
        const baseUrl = 'https://api.openai.com/v1/realtime'
        const model = 'gpt-4o-realtime-preview-2024-12-17'

        const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
          method: 'POST',
          body: peerConnection.current.localDescription.sdp,
          headers: {
            Authorization: `Bearer ${EPHEMERAL_KEY}`,
            'Content-Type': 'application/sdp',
            'OpenAI-Beta': 'realtime=v1'
          }
        })

        if (!sdpResponse.ok) {
          const errorText = await sdpResponse.text()
          throw new Error(`SDP response error: ${sdpResponse.status} - ${errorText}`)
        }

        const answer = {
          type: 'answer',
          sdp: await sdpResponse.text()
        }
        await peerConnection.current.setRemoteDescription(answer)
      } catch (error) {
        console.error('Error initializing WebRTC:', error)
        cleanup()
        message.error('Failed to connect. Please try again.')
      } finally {
        setIsConnecting(false)
      }
    },
    [cleanup, handleConnectionStateChange, setupDataChannel]
  )

  return {
    // State
    isConnecting,
    isConnected,
    isListening,
    retryCount,
    isAISpeaking,
    userSpeaking,
    sessionId,

    // Methods
    initWebRTC,
    cleanup,
    setRetryCount,
    setUserSpeaking,
    setIsAISpeaking,

    // Constants
    MAX_RETRIES
  }
}

export default useWebRTCConnection
